import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { featureService, PulseReport } from '@common/api/featureService';
import Skeleton from '@common/components/Skeleton';
import { Button, Icon } from '@common/index';
import { snapdom } from '@zumer/snapdom';
import jsPDF from 'jspdf';
import {
    ChevronLeft,
    Download
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArtifactMarkdown } from '../Canvas/ResultItem/ArtifactMarkdown';
import { WebPage } from '../Canvas/ResultItem/WebPage';
import styles from './index.module.scss';

export const PulseDetail = () => {
    const navigate = useNavigate();
    const { pulseId } = useParams();
    const [pulseDetail, setPulseDetail] = useState<PulseReport | null>(null);
    const [loading, setLoading] = useState(true);
    const {openForNextPulseModal} = useAuth();
    const {pulse, chat} = useAdminContext();
    const { setContext } = chat;

    // Download markdown file directly from OSS
    const downloadMarkdown = async () => {
        if (!pulseDetail?.oss_url) return;
        
        try {
            const response = await fetch(pulseDetail.oss_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${pulseDetail.title || 'pulse'}.md`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('下载 markdown 文件失败:', error);
        }
    };

    // Convert current rendered content to PDF using snapdom
    const downloadPDF = async () => {
        if (!pulseDetail?.oss_url) return;
        
        try {
            // Find the pulse content element that's already rendered in the browser
            const pulseContentElement = document.querySelector(`.${styles.pulseContent}`);
            if (!pulseContentElement) {
                console.error('找不到内容区域');
                return;
            }
            
            // Clone the element to ensure we capture the full content without scroll limitations
            const clonedElement = pulseContentElement.cloneNode(true) as HTMLElement;
            
            // Create a temporary container
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '-9999px';
            container.style.left = '-9999px';
            container.style.width = `${pulseContentElement.clientWidth}px`; // Keep original width
            container.style.zIndex = '-1';
            
            // Reset styles on the cloned element to ensure full height visibility
            clonedElement.style.height = 'auto';
            clonedElement.style.overflow = 'visible';
            clonedElement.style.maxHeight = 'none';
            clonedElement.style.borderRadius = '0';
            
            container.appendChild(clonedElement);
            document.body.appendChild(container);
            
            // Use snapdom to capture the cloned content
            const result = await snapdom(clonedElement, {
                scale: 2,
                backgroundColor: '#ffffff',
                embedFonts: true,
                useCORS: true,
                allowTaint: true,
            } as any);
            
            // Clean up
            document.body.removeChild(container);
            
            // Get canvas from snapdom result
            const canvas = await result.toCanvas();
            
            // Get image data from canvas
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            // Set PDF width to A4 width (210mm) and calculate height based on aspect ratio
            const pdfWidth = 210;
            const pdfHeight = (imgHeight * pdfWidth) / imgWidth;

            const pdf = new jsPDF({
                orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
                unit: 'mm',
                format: [pdfWidth, pdfHeight]
            });

            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            
            pdf.save(`${pulseDetail.title || 'pulse'}.pdf`);
        } catch (error) {
            console.error('生成 PDF 失败:', error);
        }
    };

    // 如果 pulseData 为空，尝试获取数据
    useEffect(() => {
        if (pulseId) {
            const _pulseDetail = pulse.pulseData?.find((item) => item.id === pulseId);
            if(_pulseDetail){
                setPulseDetail(_pulseDetail);
                return;
            }
            // 获取所有 pulse 数据
            const fetchPulseData = async () => {
                try {
                    setLoading(true);
                    const res = await featureService.getPulseReportDetail(
                        pulseId
                    );
                    setPulseDetail(res);
                } catch (err) {
                    console.error('Failed to fetch pulse reports:', err);
                    setPulseDetail(null);
                } finally {
                    setLoading(false);
                }
            };
            fetchPulseData();
        }
    }, [pulseId]);

    useEffect(() => {
        if(pulseDetail) {
            setContext([{
                payload: {pulseId: pulseDetail?.id || '',},
                title: pulseDetail?.title || '',
                type: 'file',
            }]);
        }
        return () => {
            setContext([]);
        }
    }, [pulseDetail]);

    const handleBack = () => {
        navigate('/pulse');
        openForNextPulseModal();
    };

    if(!loading && !pulseDetail?.oss_url){
        return (
            <div className={styles.notFound}>
                <Icon name="lock" size={48} color="#1A162F" />
                <h2>You don't have access to this Pulse</h2>
                <Button size="2" onClick={() => navigate('/')}>Back to home</Button>
            </div>
        )
    }
    
    const isHtml = pulseDetail?.oss_url?.endsWith('.html');

    return (
        <div className={styles.layout}>
            <div className={`${styles.pulseDetail}`}>
                <div className={styles.container}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <div className={styles.headerMeta}>
                                <button 
                                    className={styles.backButton}
                                    onClick={handleBack}
                                    type="button"
                                >
                                    <ChevronLeft size={18} className={styles.backIcon} />
                                    <span>Back</span>
                                </button>
                            </div>
                            
                            <h1 className={styles.title}>
                                {pulseDetail?.title || ''}
                            </h1>
                        </div>

                        <div className={styles.headerRight}>
                            {/* <button className={styles.shareButton}>
                                <Share2 size={18} />
                                Share
                            </button> */}
                            <Button 
                                className={styles.downloadButton}
                                onClick={isHtml ? downloadPDF : downloadMarkdown}
                            >
                                <Download size={18} />
                                Download 
                                {' '}
                                {isHtml ? 'PDF' : 'Markdown'}
                            </Button>
                        </div>
                    </div>

                    <div className={styles.pulseContent}>
                        {loading ? (
                            <div className={styles.skeletonWrapper}>
                                <Skeleton width="100%" height="24px" marginBottom="16px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="90%" height="16px" marginBottom="12px" />
                                <Skeleton width="95%" height="16px" marginBottom="12px" />
                                <Skeleton width="85%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="92%" height="16px" marginBottom="12px" />
                                <Skeleton width="88%" height="16px" marginBottom="12px" />
                                <Skeleton width="96%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="90%" height="16px" marginBottom="16px" />
                                <Skeleton width="60%" height="400px" marginBottom="16px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="95%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                            </div>
                        ) : null}
                        {isHtml ? (
                            <WebPage html={pulseDetail?.oss_url || ''} onLoad={() => setLoading(false)} />
                        ) : (
                            <ArtifactMarkdown url={pulseDetail?.oss_url || ''} onLoad={() => setLoading(false)} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
